import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { format, addDays } from 'date-fns';
import {
  Hotel,
  Calendar,
  Users,
  Search,
  Star,
  MapPin,
  ArrowRight,
  Loader2,
  LogIn,
  UserPlus,
  Flower2,
  CalendarDays,
  Clock,
  Building2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export default function GuestPortalPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [properties, setProperties] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [spaServices, setSpaServices] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [functionSpaces, setFunctionSpaces] = useState([]);
  
  // Search form
  const [selectedProperty, setSelectedProperty] = useState('');
  const [arrivalDate, setArrivalDate] = useState(addDays(new Date(), 1));
  const [departureDate, setDepartureDate] = useState(addDays(new Date(), 3));
  const [adults, setAdults] = useState('2');
  const [children, setChildren] = useState('0');

  useEffect(() => {
    loadProperties();
  }, []);

  // Load spa services and events when property is selected
  useEffect(() => {
    if (selectedProperty) {
      loadSpaServices(selectedProperty);
      loadUpcomingEvents(selectedProperty);
      loadFunctionSpaces(selectedProperty);
    }
  }, [selectedProperty]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await guestPortalApi.listProperties();
      setProperties(response.data || []);
      if (response.data?.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const loadSpaServices = async (propertyId) => {
    try {
      const response = await guestPortalApi.listSpaServices(propertyId);
      setSpaServices(response.data || []);
    } catch (error) {
      console.error('Failed to load spa services:', error);
    }
  };

  const loadUpcomingEvents = async (propertyId) => {
    try {
      const response = await guestPortalApi.listUpcomingEvents(propertyId);
      setUpcomingEvents(response.data || []);
    } catch (error) {
      console.error('Failed to load upcoming events:', error);
    }
  };

  const loadFunctionSpaces = async (propertyId) => {
    try {
      const response = await guestPortalApi.listFunctionSpaces(propertyId);
      setFunctionSpaces(response.data || []);
    } catch (error) {
      console.error('Failed to load function spaces:', error);
    }
  };

  const handleSearch = async () => {
    if (!selectedProperty) {
      toast.error('Please select a property');
      return;
    }

    try {
      setSearching(true);
      const response = await guestPortalApi.searchAvailability({
        property_id: selectedProperty,
        arrival_date: format(arrivalDate, 'yyyy-MM-dd'),
        departure_date: format(departureDate, 'yyyy-MM-dd'),
        adults: parseInt(adults),
        children: parseInt(children),
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search availability');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectRoom = (result) => {
    // Store selection in session and navigate to booking
    sessionStorage.setItem('bookingSelection', JSON.stringify({
      property_id: selectedProperty,
      property: properties.find(p => p.id === selectedProperty),
      room_type: result.room_type,
      rate_plan: result.rate_plan,
      arrival_date: format(arrivalDate, 'yyyy-MM-dd'),
      departure_date: format(departureDate, 'yyyy-MM-dd'),
      nights: result.nights,
      adults: parseInt(adults),
      children: parseInt(children),
      total_rate: result.total_rate,
      taxes_estimated: result.taxes_estimated,
      grand_total: result.grand_total,
    }));
    navigate('/portal/booking');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Skeleton className="h-12 w-64 mx-auto mb-8" />
        <Skeleton className="h-64 max-w-4xl mx-auto" />
      </div>
    );
  }

  return (
    <div data-testid="guest-portal-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Find Your Perfect Stay
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Discover our collection of premium hotels across Ethiopia. Book directly for the best rates.
          </p>

          {/* Search Box */}
          <Card className="max-w-4xl mx-auto" data-testid="search-box">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {/* Property Select */}
                <div className="md:col-span-2 space-y-2 text-left">
                  <Label>Property</Label>
                  <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                    <SelectTrigger data-testid="property-select">
                      <SelectValue placeholder="Select hotel" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map(prop => (
                        <SelectItem key={prop.id} value={prop.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            {prop.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Check-in Date */}
                <div className="space-y-2 text-left">
                  <Label>Check-in</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="checkin-btn">
                        <Calendar className="mr-2 h-4 w-4" />
                        {format(arrivalDate, 'MMM d')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={arrivalDate}
                        onSelect={(date) => date && setArrivalDate(date)}
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Check-out Date */}
                <div className="space-y-2 text-left">
                  <Label>Check-out</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="checkout-btn">
                        <Calendar className="mr-2 h-4 w-4" />
                        {format(departureDate, 'MMM d')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={departureDate}
                        onSelect={(date) => date && setDepartureDate(date)}
                        disabled={(date) => date <= arrivalDate}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Guests */}
                <div className="space-y-2 text-left">
                  <Label>Guests</Label>
                  <Select value={adults} onValueChange={setAdults}>
                    <SelectTrigger data-testid="guests-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4].map(n => (
                        <SelectItem key={n} value={String(n)}>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            {n} {n === 1 ? 'Guest' : 'Guests'}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                className="w-full md:w-auto mt-6" 
                size="lg"
                onClick={handleSearch}
                disabled={searching}
                data-testid="search-btn"
              >
                {searching ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Search Availability
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Search Results */}
      {searchResults && (
        <section className="py-12 bg-muted/30" data-testid="search-results">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6">
              Available Rooms ({searchResults.results?.length || 0})
            </h2>
            
            {searchResults.results?.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Hotel className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg text-muted-foreground">
                    No rooms available for the selected dates.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Try adjusting your dates or selecting a different property.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.results.map((result, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`room-card-${idx}`}>
                    <div className="aspect-video bg-muted flex items-center justify-center">
                      <Hotel className="h-16 w-16 text-muted-foreground/50" />
                    </div>
                    <CardHeader>
                      <CardTitle>{result.room_type.name}</CardTitle>
                      <CardDescription>
                        {result.room_type.description || 'Comfortable room with modern amenities'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Occupancy</span>
                          <span>Up to {result.room_type.max_occupancy} guests</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Rate Plan</span>
                          <span>{result.rate_plan.name}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{result.nights} night(s)</span>
                          <span className="font-medium">${result.rate_per_night}/night</span>
                        </div>
                        <div className="border-t pt-3">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Total</span>
                            <div className="text-right">
                              <p className="text-2xl font-bold">${result.grand_total.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">incl. taxes</p>
                            </div>
                          </div>
                        </div>
                        <Button 
                          className="w-full" 
                          onClick={() => handleSelectRoom(result)}
                          data-testid={`select-room-${idx}`}
                        >
                          Select Room
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Properties Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-6">Our Properties</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map(prop => (
              <Card key={prop.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`property-card-${prop.id}`}>
                <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <Hotel className="h-16 w-16 text-primary/50" />
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {prop.name}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {prop.address_city}, {prop.address_country}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      setSelectedProperty(prop.id);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    View Rooms
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Spa & Wellness Section */}
      {spaServices.length > 0 && (
        <section className="py-12 bg-muted/30" data-testid="spa-section">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 mb-6">
              <Flower2 className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold">Spa & Wellness</h2>
            </div>
            <p className="text-muted-foreground mb-8 max-w-2xl">
              Rejuvenate your mind and body with our premium spa services. Book your treatment during your stay.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {spaServices.slice(0, 4).map(service => (
                <Card key={service.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`spa-service-${service.id}`}>
                  <div className="aspect-square bg-gradient-to-br from-pink-100 to-purple-50 dark:from-pink-900/30 dark:to-purple-900/20 flex items-center justify-center">
                    <Flower2 className="h-16 w-16 text-pink-400/50" />
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{service.name}</CardTitle>
                    {service.description && (
                      <CardDescription className="line-clamp-2">
                        {service.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {service.duration_minutes} min
                      </span>
                      <span className="font-semibold">ETB {service.price?.toLocaleString()}</span>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => navigate('/portal/login')}
                    >
                      Book Treatment
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            {spaServices.length > 4 && (
              <div className="text-center mt-6">
                <Button variant="outline" onClick={() => navigate('/portal/login')}>
                  View All Spa Services
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Events & Meetings Section */}
      {(upcomingEvents.length > 0 || functionSpaces.length > 0) && (
        <section className="py-12" data-testid="events-section">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 mb-6">
              <CalendarDays className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold">Events & Meetings</h2>
            </div>
            <p className="text-muted-foreground mb-8 max-w-2xl">
              Host your next event with us. From intimate meetings to grand celebrations, we have the perfect venue for you.
            </p>
            
            {/* Function Spaces */}
            {functionSpaces.length > 0 && (
              <div className="mb-10">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Our Venues
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {functionSpaces.slice(0, 3).map(space => (
                    <Card key={space.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`venue-${space.id}`}>
                      <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/20 flex items-center justify-center">
                        <Building2 className="h-16 w-16 text-blue-400/50" />
                      </div>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">{space.name}</CardTitle>
                        {space.description && (
                          <CardDescription className="line-clamp-2">
                            {space.description}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {space.area_sqm && (
                            <Badge variant="outline">{space.area_sqm} sqm</Badge>
                          )}
                          {space.location && (
                            <Badge variant="outline" className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {space.location}
                            </Badge>
                          )}
                        </div>
                        {space.capacities?.length > 0 && (
                          <div className="text-sm text-muted-foreground mb-3">
                            Capacity: {Math.max(...space.capacities.map(c => c.capacity))} guests
                          </div>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => navigate('/portal/login')}
                        >
                          Inquire About Venue
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Events */}
            {upcomingEvents.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CalendarDays className="h-5 w-5" />
                  Upcoming Events
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {upcomingEvents.slice(0, 4).map(event => (
                    <Card key={event.id} className="hover:shadow-md transition-shadow" data-testid={`event-${event.id}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className="bg-primary/10 rounded-lg p-3 text-center min-w-[60px]">
                            <p className="text-xs text-muted-foreground uppercase">
                              {format(new Date(event.event_date), 'MMM')}
                            </p>
                            <p className="text-2xl font-bold">
                              {format(new Date(event.event_date), 'd')}
                            </p>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold">{event.event_name}</h4>
                            <p className="text-sm text-muted-foreground capitalize">
                              {event.event_type?.replace('_', ' ')}
                            </p>
                            {event.expected_attendance && (
                              <p className="text-sm text-muted-foreground mt-1">
                                <Users className="h-4 w-4 inline mr-1" />
                                {event.expected_attendance} expected attendees
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center mt-8">
              <Button onClick={() => navigate('/portal/login')}>
                Plan Your Event
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t py-8 bg-muted/30">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2025 HMS Hotels. All rights reserved.</p>
          <p className="mt-2">
            <a href="#" className="hover:underline">Privacy Policy</a>
            {' · '}
            <a href="#" className="hover:underline">Terms of Service</a>
            {' · '}
            <a href="#" className="hover:underline">Contact Us</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
